import { Chip, Heading, TextLink } from '@mm/components';
import { FILTER_TYPES, TagFilterItem } from '@mm/state/model/AudiencesModel';
import classNames from 'classnames';
import React from 'react';
import CheckBox from '../../../../../../../../common/components/Checkbox';
import { ThreeDotMenu } from '../../../../../../../../common/components/ThreeDotMenu/ThreeDotMenu';
import { getFormattedDisplayValue } from '../../../../../../contacts/components/audienceFilters/helpers';
import { platforms } from '../../../../../../contacts/components/FilterModals/platformsFilterModal/platformsFilterModal';
import { getFilterDetailsByType } from '../../../../../../contacts/constants/filters';
import {
  LocalFilterItem,
  LocalSystemFilterItem,
  LocalVariablesValueFilterItem,
} from '../../../../../../contacts/sharedFilterTypes';
import './FilterPreview.pcss';

type FilterItemType<T> = T;

type Props<T> = {
  className?: string;
  filter: FilterItemType<T>;
  readOnly?: boolean;
  onDelete?: (filter: FilterItemType<T>) => void;
  onEdit?: (filter: FilterItemType<T>) => void;
  disabled?: boolean;
};

const getFormattedTagFilterFields = (filter: TagFilterItem) => {
  const { operator, values = [] } = filter;
  return values.map(({ name, kind }) => ({
    field: operator,
    value: `${name} ${kind === 'otn' ? '(OTN)' : ''}`,
  }));
};

const getFormattedFilterFieldByType = (filter: LocalFilterItem) => {
  const filterTyped = filter as LocalVariablesValueFilterItem;
  const { variable, operator, value } = filterTyped;

  if (filter.filterType === FILTER_TYPES.variableValues) {
    return [
      { field: 'Attribute', value: variable },
      { field: 'Operator', value: operator },
      {
        field: 'Value(s)',
        value: getFormattedDisplayValue(value),
      },
    ];
  }

  if (filter.filterType === FILTER_TYPES.reachable) {
    return [
      {
        field: 'Reachable?',
        value: value[0] === 'true' ? 'Reachable' : 'Not Reachable',
      },
    ];
  }

  if (filter.filterType === FILTER_TYPES.platforms) {
    return [
      {
        field: 'Reachable?',
        value: value[0] === 'true' ? 'Reachable' : 'Not Reachable',
      },
    ];
  }

  if (filter.filterType === FILTER_TYPES.euLimits) {
    return [
      {
        field: 'EU Membership?',
        value: value[0] === 'true' ? 'EU' : 'Non-EU',
      },
    ];
  }

  return [];
};

export const FilterPreview = <
  T extends TagFilterItem | LocalFilterItem | LocalSystemFilterItem,
>({
  className,
  filter,
  readOnly,
  onDelete,
  onEdit,
  disabled,
}: Props<T>) => {
  const { title } = getFilterDetailsByType(filter.filterType) || {};

  let fields:
    | ReturnType<typeof getFormattedTagFilterFields>
    | ReturnType<typeof getFormattedFilterFieldByType>;

  if (filter.filterType === FILTER_TYPES.tagsFilters) {
    fields = getFormattedTagFilterFields(filter as TagFilterItem);
  } else if (filter.filterType === FILTER_TYPES.platforms) {
    fields = [];
  } else {
    fields = getFormattedFilterFieldByType(filter as LocalFilterItem);
  }

  const isEmpty = !fields || !fields.length;

  const menuElements = [
    { name: 'Edit', value: 'edit', disabled: readOnly || disabled },
    { name: 'Delete', value: 'delete', disabled: readOnly || disabled },
  ];

  const onMenuClick = (val: string) => {
    switch (val) {
      case 'delete':
        if (onDelete) {
          onDelete(filter);
        }
        break;
      case 'edit':
        if (onEdit) {
          onEdit(filter);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className={classNames(className, 'filter-preview')}>
      <Heading className="filter-preview__heading" size={4} text={title} />
      {!isEmpty && (
        <div className="filter-preview__values">
          {fields.map(field => {
            const combinedValuesStr = `${field.field}: ${field.value}`;
            if (filter.filterType === FILTER_TYPES.tagsFilters) {
              return (
                <Chip
                  key={combinedValuesStr}
                  className="filter-preview__value"
                  text={combinedValuesStr}
                />
              );
            }

            return (
              <div key={combinedValuesStr} className="filter-preview__fields">
                <TextLink className="filter-preview__filed-name">
                  {field.field}
                  {`: `}
                </TextLink>
                <TextLink className="filter-preview__filed-value">
                  {field.value}
                </TextLink>
              </div>
            );
          })}
        </div>
      )}
      {filter.filterType === FILTER_TYPES.platforms && (
        <>
          {platforms.map(platform => (
            <div className="filter-preview__check-area">
              <CheckBox
                isChecked={(filter as LocalSystemFilterItem).value.some(
                  p => p === platform.name,
                )}
                disabled
              />
              <span className="filter-preview__check-area-text">
                {platform.text}
              </span>
            </div>
          ))}
        </>
      )}
      <ThreeDotMenu
        className="filter-preview__menu"
        elements={menuElements}
        selectItem={onMenuClick}
      />
    </div>
  );
};
